/**
 * RBAC (Role-Based Access Control) Utility Functions
 *
 * Provides role-checking helper functions for the frontend.
 * These functions replace direct access to legacy boolean flags (is_admin, is_fair_manager, is_vendor)
 * with proper RBAC role checking.
 *
 * MIGRATION STATUS (Issue #860):
 * - Phase 1: Database schema with roles/permissions tables ✅
 * - Phase 2: Backend integration with auth_service RBAC methods ✅
 * - Phase 3: Frontend integration with these utility functions ✅ (THIS FILE)
 * - Phase 4: Remove legacy boolean flags (future)
 *
 * IMPORTANT NOTES:
 * - localStorage data can become stale if user roles change on backend
 * - User must logout/login or refresh session to see updated roles
 * - Consider implementing periodic session refresh mechanism
 *
 * @see docs/RBAC_MIGRATION.md
 * @see docs/AUTH.md
 */

/**
 * Get the current user object from localStorage
 * @returns User object with roles array, or null if not logged in
 */
export const getCurrentUser = (): { roles?: string[]; permissions?: Record<string, boolean> } | null => {
  const userStr = localStorage.getItem('user');
  if (!userStr) {
    return null;
  }

  try {
    return JSON.parse(userStr);
  } catch (error) {
    console.error('Failed to parse user from localStorage:', error);
    return null;
  }
};

/**
 * Check if the current user has a specific role
 * @param roleName - The name of the role to check (case-sensitive)
 * @returns true if user has the role, false otherwise
 */
export const hasRole = (roleName: string): boolean => {
  const user = getCurrentUser();
  return user?.roles?.includes(roleName) || false;
};

/**
 * Check if the current user has Admin role
 * Admins have all permissions in the system
 * @returns true if user is an Admin, false otherwise
 */
export const isAdmin = (): boolean => {
  return hasRole('Admin');
};

/**
 * Check if the current user has Fair Manager role (or Admin)
 * Admins automatically have Fair Manager permissions
 * @returns true if user is a Fair Manager or Admin, false otherwise
 */
export const isFairManager = (): boolean => {
  return hasRole('Fair Manager') || isAdmin();
};

/**
 * Check if the current user has Vendor role
 *
 * NOTE: Does NOT include implicit admin bypass (unlike isFairManager).
 * This is consistent with backend behavior. Admins must be checked
 * separately if needed (e.g., `isVendor() || isAdmin()`).
 *
 * @returns true if user has Vendor role, false otherwise
 */
export const isVendor = (): boolean => {
  return hasRole('Vendor');
};

/**
 * Check if the current user has a specific permission
 * @param permission - The permission key to check
 * @returns true if user has the permission, false otherwise
 */
export const hasPermission = (permission: string): boolean => {
  const user = getCurrentUser();
  if (!user?.permissions) {
    return false;
  }

  // Check for 'all' permission (typically granted to admins)
  if (user.permissions['all']) {
    return true;
  }

  // Check for specific permission
  return user.permissions[permission] || false;
};

/**
 * Get all roles for the current user
 * @returns Array of role names, or empty array if no roles/not logged in
 */
export const getUserRoles = (): string[] => {
  const user = getCurrentUser();
  return user?.roles || [];
};

/**
 * Get all permissions for the current user
 * @returns Object mapping permission names to boolean values, or empty object if no permissions/not logged in
 */
export const getUserPermissions = (): Record<string, boolean> => {
  const user = getCurrentUser();
  return user?.permissions || {};
};

/**
 * Check if user is authenticated (has a valid user object in localStorage)
 * @returns true if user is logged in, false otherwise
 */
export const isAuthenticated = (): boolean => {
  return getCurrentUser() !== null;
};

/**
 * Clear user data from localStorage (used during logout)
 */
export const clearUserData = (): void => {
  localStorage.removeItem('user');
  // Legacy flag cleanup removed in Phase 4 (Issue #865)
  // All role checking now uses RBAC system exclusively
};
